import { useMemo } from "react";
import {
  METRICS_EVENTS,
  TradeFailedParams,
  TradeMethod,
  TradeSubmittedParams,
  WidgetConfig,
  TradeExecutedPayload,
  TradeFailedPayload,
  CAMPAIGN_PLACEHOLDER_NAME,
  FEATURE_FLAGS,
} from "@cede/types";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useReferrals } from "@/hooks/referral/useReferrals";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useTradingViewStore } from "@/store/useTradingViewStore";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";

export const useStandaloneTradeConfig = () => {
  const { hubUser, auth, authenticated } = useHubUser();
  const { trackEvent } = useMetricsEvents();
  const isTradeExecutedReportingEnabled = useFeatureFlagIsOn(
    FEATURE_FLAGS.TRADE_REPORTING_API_TRADE_EXECUTED_EVENT_ENABLE,
  );
  const searchParams = useSearchParams();
  const { exchange } = useTradingViewStore();
  const queryClient = useQueryClient();
  const { refereeReferral } = useReferrals({ mode: "minimal", enabled: authenticated });

  const tradeWidgetConfig = useMemo(() => {
    if (authenticated && hubUser?.defiAddress) {
      // Get query parameters for preselection
      const tokenToParam = searchParams.get("tokenTo"); // Token to buy (e.g., "KCS", "BNB")
      const tokenFromParam = searchParams.get("tokenFrom"); // Token to sell/trade from
      const exchangeParam = searchParams.get("exchange");

      // Enable auto-select if we have URL params, even if store doesn't have exchange yet
      const hasUrlParams = !!(exchangeParam && (tokenToParam || tokenFromParam));
      const autoSelectTokensEnabled = hasUrlParams || exchange?.exchangeId === exchangeParam;

      const config: WidgetConfig = {
        tokenSymbols: undefined,
        // Allow all supported exchanges
        exchangeIds: Object.values(SUPPORTED_EXCHANGES),
        // Set preferred exchange from query param
        preferredExchange:
          exchangeParam &&
          Object.values(SUPPORTED_EXCHANGES).includes(
            exchangeParam as (typeof SUPPORTED_EXCHANGES)[keyof typeof SUPPORTED_EXCHANGES],
          )
            ? exchangeParam
            : undefined,
        limitOrderExchangeIds: [],
        // Set recommendedToken to the native token we want to buy
        recommendedToken: tokenToParam || undefined,
        // Set recommendedTokenFrom to pre-select the token to sell
        recommendedTokenFrom: tokenFromParam || undefined,
        // Allow all tokens to be available, not just the recommended one
        recommendedTokenOnly: false,
        isHub: true,
        defiAddress: hubUser.defiAddress,
        auth,
        keepPreviousTradeValues: false,
        campaignId: CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
        campaignMetadata: undefined,
        autoSelectTokens: autoSelectTokensEnabled,
      };

      config.onTradeSubmitted = async (params: TradeSubmittedParams) => {
        if (!isTradeExecutedReportingEnabled) {
          return;
        }

        const { order, orderParams, exchangeId, exchangeUserId, baseFiatAmount, orderBookComputedValues } = params;

        const referralProperties = {
          referrerId: refereeReferral?.referrerId,
          referralStatus: refereeReferral?.status,
        };

        const mixpanelPayload = {
          fromTradePage: true,
          id: order.id,
          exchangeId: exchangeId || "unknown", // it should be defined
          exchangeUserId,
          pairSymbol: order.pairSymbol,
          expectedPrice: orderParams.price,
          amount: orderParams.amount,
          type: order.type,
          side: order.side,
          status: order.status,
          timestamp: order.timestamp,
          tradingMethod: TradeMethod.HUB,
          price: order.price,
          baseAmount: order.amount,
          baseFiatAmount: baseFiatAmount || "unknown",
          walletAddress: hubUser?.defiAddress ?? undefined,
          chain: "offchain",
          ...(orderBookComputedValues || {}),
          ...referralProperties,
          marketType: "spot", // for now we only support spot trades for CEXs
        } satisfies TradeExecutedPayload;
        trackEvent(METRICS_EVENTS.TRADE_EXECUTED, mixpanelPayload);
      };

      config.onTradeFailed = (params: TradeFailedParams) => {
        const { orderParams, exchangeId, errorMessage } = params;

        const referralProperties = {
          referrerId: refereeReferral?.referrerId,
          referralStatus: refereeReferral?.status,
        };

        const mixpanelPayload = {
          fromTradePage: true,
          exchangeId: exchangeId || "unknown", // it should be defined
          pairSymbol: orderParams.pairSymbol,
          price: orderParams.price,
          amount: orderParams.amount,
          type: orderParams.orderType,
          side: orderParams.orderSide,
          baseAmount: orderParams.amount,
          tradingMethod: TradeMethod.HUB,
          errorMessage: errorMessage || "unknown error", // it should be defined
          walletAddress: hubUser?.defiAddress ?? undefined,
          chain: "offchain",
          ...referralProperties,
          marketType: "spot", // for now we only support spot trades for CEXs
        } satisfies TradeFailedPayload;
        trackEvent(METRICS_EVENTS.TRADE_FAILED, mixpanelPayload);
      };

      return config;
    }
    return null;
  }, [
    authenticated,
    hubUser?.defiAddress,
    auth,
    searchParams,
    exchange,
    refereeReferral?.referrerId,
    refereeReferral?.status,
    queryClient,
    trackEvent,
    isTradeExecutedReportingEnabled,
  ]);

  return tradeWidgetConfig;
};
